import {
  ConversationDTO,
  ConversationMessage,
  ConversationStatus,
  RepliedToMessage,
} from './api/models/ConversationDTO';
import { RegisteredCaregiverDTO } from './api/models/RegisteredCaregiverDTO';
import { UserDTO } from './api/models/UserDTO';

export type WebSocketDataChatMessage = {
  id: number;
  message_text: string;
  sender: {
    display_name: string;
    first_name: string;
    id: string;
    is_caregiver: boolean;
    photo_url: string;
    number_of_conversations_as_patient: number;
  };
  sent_at: string;
  conversation: string;
  replied_to_message?: RepliedToMessage;
  is_deleted?: boolean;
  edited_at?: string | null;
};

export type WebSocketDataMessageEdited = WebSocketDataChatMessage;
export type WebSocketDataMessageDeleted = WebSocketDataChatMessage;
export type WebSocketDataReadNotification = {
  conversation: string;
  reader: string;
  last_read_datetime: string;
};
export type WebSocketDataConversationRequestToGroup = {
  current_owner: UserDTO;
  id: string;
  last_message: ConversationMessage;
  patient: UserDTO;
  number_of_unread_messages: number;
  status: ConversationStatus;
};
export type WebSocketDataConversationRequestToCaregiver = {
  current_owner: UserDTO;
  id: string;
  last_message: ConversationMessage;
  patient: UserDTO;
  number_of_unread_messages: number;
  status: ConversationStatus;
};
export type WebSocketDataConversationAssigned = {
  id: string; // Id of the conversation
  new_owner: string; // Id of the user
  current_owner?: UserDTO; // Only present in the patient-facing variant of this event
  referent_caregiver?: RegisteredCaregiverDTO | null; // Patient-facing too: her referring caregiver, who may have just changed
};
export type WebSocketDataConversationStatusUpdated = {
  id: string;
  new_status: string;
};
// Used for both `conversation.created_by_patient` (patient just created their
// conversation — the Shell handler also navigates them into it) and
// `conversation.became_visible` (a conversation has just become visible to the
// patient because a different caregiver picked it up and sent the first message
// — idempotent upsert into the list, no navigation). Same payload shape: a row
// from the list endpoint.
export type WebSocketDataConversationCreated = ConversationDTO;

/** A candidate reply suggestion (GYN-266) became available for a conversation
 * this caregiver currently owns - pushed only to them, not broadcast. */
export type WebSocketDataReplySuggestionCreated = {
  id: number;
  suggestion: string;
  conversation: string;
};

export type WebSocketData = {
  type:
    | 'chat_message'
    | 'chat_message_edited'
    | 'chat_message_deleted'
    | 'unassigned_message'
    | 'conversation_assigned'
    | 'conversation.assignment.asked_group'
    | 'conversation.assignment.asked_caregiver'
    | 'conversation.assignment.rejected'
    | 'conversation.assignment.timed_out'
    | 'conversation.read_notification'
    | 'conversation.status.updated'
    | 'conversation.created_by_patient'
    | 'conversation.became_visible'
    /** A patient completed their questionnaire; payload is a DoctorConversationDTO row. */
    | 'questionnaire.completed'
    /** The review phase of a patient's questionnaire changed; payload is a QuestionnaireResponseDTO. */
    | 'questionnaire.status_updated'
    /** A reply suggestion is ready for the caregiver currently owning the conversation. */
    | 'reply_suggestion.created';
  payload:
    | WebSocketDataChatMessage
    | WebSocketDataConversationAssigned
    | WebSocketDataConversationRequestToGroup
    | WebSocketDataConversationRequestToCaregiver
    | WebSocketDataConversationStatusUpdated
    | WebSocketDataReadNotification
    | WebSocketDataConversationCreated
    | WebSocketDataReplySuggestionCreated;
};

export type WebSocketServiceCtorParams = {
  accessToken?: string;
  onError?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  onMessage?: (data: WebSocketData) => void;
};

const WEB_SOCKET_RECONNECT_TIMEOUT_MS = 5000;

export default class WebSocketService {
  private static instance: WebSocketService;

  private chatSocket?: WebSocket;

  private static timeouts: ReturnType<typeof setTimeout>[] = [];

  private constructor(params: WebSocketServiceCtorParams) {
    this.chatSocket = this.connectWebSocket(params);
  }

  protected connectWebSocket(params: WebSocketServiceCtorParams) {
    if (WebSocketService.instance && WebSocketService.instance.chatSocket) {
      return WebSocketService.instance.chatSocket;
    }

    const wsUrl = import.meta.env.VITE_WEB_SOCKET_URL;

    const chatSocket = new WebSocket(`${wsUrl}?token=${params.accessToken}`);

    chatSocket.onopen = () => {
      if (params.onOpen) {
        params.onOpen();
      }
    };

    chatSocket.onerror = () => {
      if (params.onError) {
        params.onError();
      }

      chatSocket.close();
    };

    chatSocket.onclose = () => {
      if (params.onClose) {
        params.onClose();
      }
    };

    // Reconnect if the Web Socket connection is lost
    const timeout = setInterval(() => {
      if (
        WebSocketService.instance &&
        WebSocketService.instance.chatSocket &&
        WebSocketService.instance.chatSocket.readyState === WebSocketService.instance.chatSocket.CLOSED
      ) {
        WebSocketService.getNewInstance(params);
      }
    }, WEB_SOCKET_RECONNECT_TIMEOUT_MS);
    WebSocketService.timeouts = Array.from(WebSocketService.timeouts.concat(timeout));

    chatSocket.onmessage = event => {
      const data = JSON.parse(event.data);

      if (params.onMessage) {
        params.onMessage(data);
      }
    };

    return chatSocket;
  }

  static getInstance = (params: WebSocketServiceCtorParams): WebSocketService => {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService(params);
    }

    return WebSocketService.instance;
  };

  static getNewInstance = (params: WebSocketServiceCtorParams): WebSocketService => {
    // Close previous Web Socket connection
    WebSocketService.close();

    WebSocketService.instance = new WebSocketService(params);

    return WebSocketService.instance;
  };

  static close = () => {
    if (WebSocketService.timeouts && WebSocketService.timeouts.length > 0) {
      WebSocketService.timeouts.forEach(timeout => clearInterval(timeout));
      WebSocketService.timeouts = [];
    }

    if (WebSocketService.instance) {
      if (
        WebSocketService.instance.chatSocket &&
        WebSocketService.instance.chatSocket.readyState === WebSocketService.instance.chatSocket.OPEN
      ) {
        WebSocketService.instance.chatSocket.close();
      }
      WebSocketService.instance.chatSocket = undefined;
    }
  };
}
